<template>
    <slot :anchor-props="anchorProps" />

    <teleport to="#teleport">
        <transition
            enter-active-class="transition ease-out duration-100"
            enter-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <popup-menu-render
                v-if="controlledExpanded"
                ref="popupRef"
                :anchor-ref="anchorRef"
                :menu-id="menuId"
                :anchor-id="anchorId"
                :margin-class="marginClass"
                @keydown.esc="handleEscape"
            >
                <slot name="content" />
            </popup-menu-render>
        </transition>
    </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import PopupMenuRender from '~/components/PopupMenu/PopupMenuRender.vue'
import { useControlledState } from '~/composables/use-controlled-state'

let popupMenuNumber = 0

export default defineComponent({
    name: 'PopupMenu',

    components: { PopupMenuRender },

    inheritAttrs: false,

    props: {
        ignorePopup: {
            type: Boolean,
            default: false
        },
        // eslint-disable-next-line vue/no-unused-properties
        expanded: {
            type: Boolean,
            default: () => undefined
        },
        interactive: {
            type: Boolean,
            default: false
        },
        marginClass: {
            type: String,
            default: 'mt-2'
        }
    },

    emits: {
        'update:expanded': (payload: boolean) => typeof payload === 'boolean',
        escape: (event: KeyboardEvent | PointerEvent) => true
    },

    setup(props, { emit, attrs }) {
        popupMenuNumber += 1
        const randomId = 'popup-menu-' + popupMenuNumber
        const anchorId = randomId + '-anchor'
        const menuId = randomId + '-menu'
        const { isControlled, stateRef } = useControlledState(
            'expanded',
            props,
            false
        )
        const controlledExpanded = computed({
            get() {
                return stateRef.value
            },
            set(newValue) {
                if (isControlled) {
                    emit('update:expanded', newValue)
                } else {
                    stateRef.value = newValue
                }
            }
        })

        const anchorRef = ref()
        const popupRef = ref()

        const anchorProps = computed(() => {
            return {
                ref: anchorRef,
                id: anchorId,
                expanded: controlledExpanded.value,
                'aria-haspopup': 'true',
                'aria-expanded': controlledExpanded.value ? 'true' : 'false',
                'aria-controls': menuId,
                onClick: (event: MouseEvent) => {
                    if (!event.defaultPrevented) {
                        event.preventDefault()
                        controlledExpanded.value = !controlledExpanded.value
                    }
                },
                onKeydown: handleEscape,
                ...attrs
            }
        })

        onClickOutside(
            anchorRef,
            event => {
                if (!controlledExpanded.value) {
                    return
                }

                // I don't know why these were not working in the ignore below
                if (
                    Array.from(
                        window.document.querySelectorAll('.dp__menu')
                    ).some(el => event.composedPath().includes(el))
                ) {
                    return
                }

                // When closing, if we hit a button, we probably wanted to save the input
                if (event.target?.tagName !== 'BUTTON') {
                    // So we don't escape
                    emit('escape', event)
                }

                // Close
                controlledExpanded.value = false
            },
            {
                ignore:
                    props.ignorePopup || props.interactive
                        ? [
                              popupRef,
                              '.js-SuperSelectList',
                              '.dp__menu',
                              '.js-PopupMenuInteractive'
                          ]
                        : []
            }
        )

        function handleEscape(event: KeyboardEvent) {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                event.preventDefault()
                emit('escape', event)
                controlledExpanded.value = false
                nextTick().then(() => {
                    if (anchorRef.value !== undefined) {
                        if (anchorRef.value.focus !== undefined) {
                            anchorRef.value.focus()
                        } else {
                            anchorRef.value.querySelector('input')?.focus?.()
                        }
                    }
                })
            }
        }

        return {
            controlledExpanded,
            anchorProps,
            anchorRef,
            popupRef,
            menuId,
            anchorId,
            handleEscape
        }
    }
})
</script>
